import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./HeatmapDashboard.css";
import redirectURL from "../../redirectURL";

import "@ag-grid-community/all-modules/dist/styles/ag-grid.css";
import "@ag-grid-community/all-modules/dist/styles/ag-theme-balham.css";
import { AgGridReact } from "@ag-grid-community/react";
import { DashboardHeaderBanner } from "./DashboardHeaderSlot.jsx";

import { LicenseManager, AllModules } from "@ag-grid-enterprise/all-modules";
import LICENSE_KEY from "../../../common/agGridLicense.js";
import Select from "react-select";
import {
  getHyphenDDMMMYYYYHHMM,
} from "../../common/utils.js";

LicenseManager.setLicenseKey(LICENSE_KEY);

function formatDateForApi(value) {
  if (!value) return "";
  var d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  var dd = String(d.getDate()).padStart(2, "0");
  var mm = String(d.getMonth() + 1).padStart(2, "0");
  var yyyy = d.getFullYear();
  return dd + "-" + mm + "-" + yyyy;
}

function formatInputDate(value) {
  if (!value) return "";
  var d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  var dd = String(d.getDate()).padStart(2, "0");
  var mm = String(d.getMonth() + 1).padStart(2, "0");
  var yyyy = d.getFullYear();
  return yyyy + "-" + mm + "-" + dd;
}

// Default range: one year back from today up to today
function defaultFromDate() {
  var d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return formatInputDate(d);
}

function parseInputDate(value, endOfDay) {
  if (!value) return null;
  var d = new Date(value + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d;
}

function formatRefreshTime(date) {
  if (!date) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDateTimeCell(value) {
  if (!value) return "";
  var d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function numberFormatter(params) {
  var n = Number(params.value || 0);
  if (isNaN(n)) return "0.0";
  return n.toFixed(1);
}

function buildFilterPayload(filterModel) {
  var out = {};
  var keys = Object.keys(filterModel || {});

  for (var i = 0; i < keys.length; i++) {
    var field = keys[i];
    var filter = filterModel[field];
    if (!filter) continue;

    if (filter.operator && filter.condition1) {
      out[field] = {
        filterType: filter.filterType,
        operator: filter.operator,
        condition1: filter.condition1,
        condition2: filter.condition2,
      };
      continue;
    }

    if (filter.filterType === "set") {
      out[field] = {
        filterType: "set",
        values: Array.isArray(filter.values) ? filter.values : [],
      };
      continue;
    }

    if (filter.filterType === "text") {
      out[field] = {
        filterType: "text",
        type: filter.type,
        filter: filter.filter,
      };
      continue;
    }

    if (filter.filterType === "number") {
      out[field] = {
        filterType: "number",
        type: filter.type,
        filter: filter.filter,
        filterTo: filter.filterTo,
      };
      continue;
    }

    if (filter.filterType === "date") {
      out[field] = {
        filterType: "date",
        type: filter.type,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
      };
    }
  }

  return out;
}

function getDateComparator(filterLocalDateAtMidnight, cellValue) {
  if (!cellValue) return -1;
  var cellDate = new Date(cellValue);
  if (isNaN(cellDate.getTime())) return -1;

  var cellOnlyDate = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate()
  );

  if (cellOnlyDate.getTime() === filterLocalDateAtMidnight.getTime()) return 0;
  if (cellOnlyDate < filterLocalDateAtMidnight) return -1;
  return 1;
}

function normalizeSelectionWithAll(selected, actionMeta) {
  var values = Array.isArray(selected) ? selected.slice() : [];

  if (!values.length) {
    return ["all"];
  }

  var lastPicked =
    actionMeta && actionMeta.option && actionMeta.option.value
      ? actionMeta.option.value
      : null;

  if (lastPicked === "all") {
    return ["all"];
  }

  values = values.filter(function (item) {
    return item && item.value !== "all";
  });

  if (!values.length) {
    return ["all"];
  }

  var out = [];
  var seen = {};

  for (var i = 0; i < values.length; i++) {
    var val = values[i] && values[i].value ? values[i].value : "";
    if (val && !seen[val]) {
      seen[val] = true;
      out.push(val);
    }
  }

  return out.length ? out : ["all"];
}

export default function GRLevelSummary() {
  var gridRef = useRef(null);
  var filterValuesCacheRef = useRef(new Map());

  var [loading, setLoading] = useState(false);
  var [exporting, setExporting] = useState(false);
  var [lastRefreshTime, setLastRefreshTime] = useState(null);
  var [totalRecords, setTotalRecords] = useState(0);

  var [whHubMaster, setWhHubMaster] = useState([]);
  var [selectedWarehouse, setSelectedWarehouse] = useState(["all"]);
  var [selectedHubLocation, setSelectedHubLocation] = useState(["all"]);

  var [fromDate, setFromDate] = useState(function () {
    return defaultFromDate();
  });
  var [toDate, setToDate] = useState(function () {
    return formatInputDate(new Date());
  });

  var [filterToggle, setFilterToggle] = useState(true);

  var warehouses = useMemo(function () {
    var set = new Set();
    for (var i = 0; i < whHubMaster.length; i++) {
      var w = whHubMaster[i] && whHubMaster[i].warehouse;
      if (w && w !== "all") set.add(w);
    }
    return Array.from(set).sort(function (a, b) {
      return String(a).localeCompare(String(b));
    });
  }, [whHubMaster]);

  var selectedWarehouseIsAll = useMemo(function () {
    return (
      !Array.isArray(selectedWarehouse) ||
      selectedWarehouse.length === 0 ||
      selectedWarehouse.indexOf("all") !== -1
    );
  }, [selectedWarehouse]);

  var selectedHubIsAll = useMemo(function () {
    return (
      !Array.isArray(selectedHubLocation) ||
      selectedHubLocation.length === 0 ||
      selectedHubLocation.indexOf("all") !== -1
    );
  }, [selectedHubLocation]);

  var hubLocations = useMemo(function () {
    var set = new Set();
    var i;
    var j;

    if (selectedWarehouseIsAll) {
      for (i = 0; i < whHubMaster.length; i++) {
        var hubs = whHubMaster[i] && whHubMaster[i].hub;
        if (!Array.isArray(hubs)) continue;
        for (j = 0; j < hubs.length; j++) {
          if (hubs[j] && hubs[j] !== "all") set.add(hubs[j]);
        }
      }
    } else {
      var selectedWarehouseSet = new Set(selectedWarehouse);

      for (i = 0; i < whHubMaster.length; i++) {
        var row = whHubMaster[i];
        if (
          row &&
          selectedWarehouseSet.has(row.warehouse) &&
          Array.isArray(row.hub)
        ) {
          for (j = 0; j < row.hub.length; j++) {
            if (row.hub[j] && row.hub[j] !== "all") set.add(row.hub[j]);
          }
        }
      }
    }

    return Array.from(set).sort(function (a, b) {
      return String(a).localeCompare(String(b));
    });
  }, [whHubMaster, selectedWarehouse, selectedWarehouseIsAll]);

  useEffect(
    function () {
      if (selectedHubIsAll) return;

      var exists = [];
      for (var i = 0; i < selectedHubLocation.length; i++) {
        if (hubLocations.indexOf(selectedHubLocation[i]) !== -1) {
          exists.push(selectedHubLocation[i]);
        }
      }

      if (!exists.length) {
        setSelectedHubLocation(["all"]);
        return;
      }

      if (exists.length !== selectedHubLocation.length) {
        setSelectedHubLocation(exists);
      }
    },
    [selectedHubLocation, selectedHubIsAll, hubLocations]
  );

  var fetchWarehousesAndHubsFromApi = useCallback(async function () {
    var response = await redirectURL.post("/msildashboard/fetchWarehousesAndHubsFrGr", {});
    var data = response && response.data ? response.data.data : null;
    if (!Array.isArray(data)) return [];
    return data;
  }, []);

  useEffect(
    function () {
      var isMounted = true;

      async function loadMaster() {
        try {
          var master = await fetchWarehousesAndHubsFromApi();
          if (!isMounted) return;

          var cleaned = [];
          for (var i = 0; i < master.length; i++) {
            var item = master[i];
            if (!item || !item.warehouse) continue;
            cleaned.push({
              warehouse: String(item.warehouse),
              hub: Array.isArray(item.hub) ? item.hub.map(String) : [],
            });
          }

          setWhHubMaster(cleaned);
        } catch (e) {
          console.error(e);
          if (!isMounted) return;
          setWhHubMaster([]);
        }
      }

      loadMaster();

      return function () {
        isMounted = false;
      };
    },
    [fetchWarehousesAndHubsFromApi]
  );

  useEffect(
    function () {
      filterValuesCacheRef.current.clear();
    },
    [fromDate, toDate, selectedWarehouse, selectedHubLocation]
  );

  var getBasePayload = useCallback(
    function () {
      return {
        warehouse: selectedWarehouseIsAll ? "all" : selectedWarehouse,
        hub: selectedHubIsAll ? "all" : selectedHubLocation,
        from_date: formatDateForApi(parseInputDate(fromDate, false)),
        to_date: formatDateForApi(parseInputDate(toDate, true)),
      };
    },
    [
      selectedWarehouse,
      selectedHubLocation,
      selectedWarehouseIsAll,
      selectedHubIsAll,
      fromDate,
      toDate,
    ]
  );

  var selectStyles = useMemo(function () {
    return {
      control: function (base, state) {
        return {
          ...base,
          minHeight: "32px",
          borderRadius: "8px",
          borderColor: state.isFocused ? "#1976d2" : "#cfd8dc",
          boxShadow: state.isFocused ? "0 0 0 3px rgba(25,118,210,0.12)" : "none",
          fontSize: "12px",
        };
      },
      valueContainer: function (base) {
        return {
          ...base,
          padding: "2px 8px",
          maxHeight: "72px",
          overflowY: "auto",
        };
      },
      input: function (base) {
        return {
          ...base,
          margin: 0,
          padding: 0,
        };
      },
      indicatorsContainer: function (base) {
        return {
          ...base,
          minHeight: "32px",
        };
      },
      menu: function (base) {
        return {
          ...base,
          fontSize: "12px",
          zIndex: 9999,
        };
      },
      menuPortal: function (base) {
        return {
          ...base,
          zIndex: 9999,
        };
      },
      option: function (base) {
        return {
          ...base,
          fontSize: "12px",
        };
      },
      multiValue: function (base) {
        return {
          ...base,
          fontSize: "12px",
        };
      },
      singleValue: function (base) {
        return {
          ...base,
          fontSize: "12px",
        };
      },
      placeholder: function (base) {
        return {
          ...base,
          fontSize: "12px",
        };
      },
    };
  }, []);

  var getFilterValuesOnce = useCallback(
    async function (params) {
      var column = params.colDef.field;
      var key = [
        column,
        Array.isArray(selectedWarehouse) ? selectedWarehouse.join(",") : "all",
        Array.isArray(selectedHubLocation) ? selectedHubLocation.join(",") : "all",
        fromDate,
        toDate,
      ].join("__");

      if (filterValuesCacheRef.current.has(key)) {
        params.success(filterValuesCacheRef.current.get(key));
        return;
      }

      try {
        var payload = getBasePayload();
        payload.column = column;

        if (gridRef.current && gridRef.current.api && gridRef.current.api.getFilterModel) {
          payload.filterModel = buildFilterPayload(gridRef.current.api.getFilterModel() || {});
        }

        var response = await redirectURL.post("/msildashboard/filter-values-gr-level", payload);
        var values =
          response && response.data && Array.isArray(response.data.values)
            ? response.data.values
            : [];

        filterValuesCacheRef.current.set(key, values);
        params.success(values);
      } catch (e) {
        console.error("filter-values-gr-level API failed", e);
        params.success([]);
      }
    },
    [selectedWarehouse, selectedHubLocation, fromDate, toDate, getBasePayload]
  );

  var setFilterParams = useMemo(
    function () {
      return function () {
        return {
          values: function (params) {
            getFilterValuesOnce(params);
          },
          refreshValuesOnOpen: true,
          suppressSyncValuesAfterDataChange: true,
          suppressMiniFilter: false,
        };
      };
    },
    [getFilterValuesOnce]
  );

  var dateFilterParams = useMemo(
    function () {
      return {
        comparator: getDateComparator,
        browserDatePicker: true,
        minValidYear: 2000,
        maxValidYear: 2100,
        inRangeInclusive: true,
      };
    },
    []
  );

  var columnDefs = useMemo(
    function () {
      function textCol(headerName, field, minWidth, pinned) {
        return {
          headerName: headerName,
          field: field,
          minWidth: minWidth,
          pinned: pinned,
          headerClass: "ag-left-header",
          cellClass: "ag-left-cell",
          filter: "agSetColumnFilter",
          filterParams: setFilterParams(),
        };
      }
      function dateCol(headerName, field) {
        var col = textCol(headerName, field, 190);
        col.valueFormatter = function (p) {
          if (!p || !p.value) return "";
          return getHyphenDDMMMYYYYHHMM(p.value);
        };
        return col;
      }
      // decimals: undefined = value as stored (hours), else fixed decimals (costs, volumes)
      function numCol(headerName, field, minWidth, decimals) {
        return {
          headerName: headerName,
          field: field,
          minWidth: minWidth || 140,
          headerClass: "ag-center-header",
          cellClass: "ag-center-cell",
          filter: "agSetColumnFilter",
          filterParams: setFilterParams(),
          valueFormatter:
            decimals === undefined
              ? undefined
              : function (p) {
                  if (!p || p.value === null || p.value === undefined || p.value === "") return "";
                  var n = Number(p.value);
                  return isNaN(n) ? String(p.value) : n.toFixed(decimals);
                },
        };
      }

      return [
        textCol("WRHS", "consigner_code", 110, "left"),
        textCol("Hub", "hub_location", 120, "left"),
        textCol("GR No", "consignment_code", 170, "left"),
        textCol("Dealer Name", "consignee_name", 220),
        textCol("City", "consignee_city", 130),
        textCol("Hub Operator Name", "hub_operator_name", 200),

        // Timings
        dateCol("MSIL Gateout Date & Time", "gate_out_time"),
        dateCol("Hub Reached Date & Time", "recent_dealer_reported"),
        dateCol("Hub left Date & Time", "dispatch_time_from_hub_from_lastmile"),
        dateCol("Consignee Delivery Date and Time", "delivery_time_from_lastmile"),

        numCol("Delivery Duration (Hr)", "total_transit_time", 170),
        numCol("First Mile (Hr)", "first_mile_transit_time"),
        numCol("Last Mile (Hr)", "last_mile_transit_time"),
        // Consignee holidays removed from the times above (Consignee Holiday Calendar master)
        Object.assign(numCol("Holiday Hrs", "holiday_hours", 120), {
          valueFormatter: function (p) {
            var n = Number(p && p.value);
            return !p || p.value === null || p.value === undefined || p.value === "" || isNaN(n) ? "0" : String(n);
          },
        }),
        textCol("Holiday Date(s)", "holiday_dates", 170),

        // Costs (latest cost schema written by process_gr_level_data_from_storage)
        {
          headerName: "Freight Type",
          field: "freight_type",
          minWidth: 140,
          headerClass: "ag-center-header",
          cellClass: "ag-center-cell",
          filter: "agSetColumnFilter",
          filterParams: setFilterParams(),
        },
        numCol("Base Cost", "base_cost", 130, 1),
        numCol("Trip Cost", "trip_cost", 130, 1),
        numCol("GR Trip Cost", "gr_level_trip_cost", 140, 1),
        numCol("Base Courier Cost (per CMT)", "courier_base_cost", 200, 1),
        numCol("Courier Trip Cost", "courier_trip_cost", 160, 1),
        numCol("Courier GR Cost", "gr_level_courier_cost", 150, 1),
        numCol("Savings", "savings", 130, 1),

        // Truck / volumes
        textCol("Truck No", "truck_no", 140),
        {
          headerName: "Truck Type",
          field: "truck_type",
          minWidth: 160,
          headerClass: "ag-center-header",
          cellClass: "ag-center-cell",
          filter: "agSetColumnFilter",
          filterParams: setFilterParams(),
        },
        numCol("GR Volume", "volume", 130, 1),
        numCol("Trip Volume", "trip_volume", 130, 1),
        numCol("Billed Volume", "freight_calculation_cmt", 140, 1),
        textCol("Remarks", "remark", 280),
      ];
    },
    [setFilterParams, dateFilterParams]
  );

  var defaultColDef = useMemo(function () {
    return {
      sortable: true,
      filter: true,
      editable: false,
      resizable: true,
      minWidth: 100,
      floatingFilter: true,
    };
  }, []);

  var datasource = useMemo(
    function () {
      return {
        getRows: async function (params) {
          setLoading(true);
          try {
            var request = params.request || {};
            var payload = getBasePayload();

            payload.startRow = request.startRow;
            payload.endRow = request.endRow;
            payload.sortModel = request.sortModel || [];
            payload.filterModel = buildFilterPayload(request.filterModel || {});

            var response = await redirectURL.post(
              "/msildashboard/getGRLevelSummaryDetails",
              payload
            );

            var responseData = response && response.data ? response.data : null;

            if (!responseData || responseData.status !== "success") {
              params.failCallback();
              return;
            }

            var rows = Array.isArray(responseData.records) ? responseData.records : [];
            var totalCount = Number(responseData.totalCount || 0);

            setTotalRecords(totalCount);
            setLastRefreshTime(new Date());

            params.successCallback(rows, totalCount);
          } catch (e) {
            console.error("getGRLevelSummaryDetails API failed", e);
            params.failCallback();
          } finally {
            setLoading(false);
          }
        },
      };
    },
    [getBasePayload]
  );

  var onGridReady = useCallback(
    function (params) {
      if (params.api && params.api.setServerSideDatasource) {
        params.api.setServerSideDatasource(datasource);
      }
    },
    [datasource]
  );

  useEffect(
    function () {
      if (!gridRef.current || !gridRef.current.api) return;
      if (gridRef.current.api.setServerSideDatasource) {
        gridRef.current.api.setServerSideDatasource(datasource);
      }
    },
    [datasource]
  );

  var clearFilters = useCallback(function () {
    setSelectedWarehouse(["all"]);
    setSelectedHubLocation(["all"]);
    setFromDate(defaultFromDate());
    setToDate(formatInputDate(new Date()));

    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
      gridRef.current.api.setSortModel([]);
    }
  }, []);

  var handleExport = useCallback(
    async function () {
      try {
        setExporting(true);
        var payload = getBasePayload();

        if (gridRef.current && gridRef.current.api) {
          payload.filterModel = buildFilterPayload(gridRef.current.api.getFilterModel() || {});
          payload.sortModel =
            (gridRef.current.api.getSortModel && gridRef.current.api.getSortModel()) || [];
        } else {
          payload.filterModel = {};
          payload.sortModel = [];
        }

        var response = await redirectURL.post(
          "/msildashboard/exportGRLevelSummaryData",
          payload,
          { responseType: "blob" }
        );

        var blob = new Blob([response.data], {
          type: "text/csv",
        });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = "gr_level_summary_export.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Export failed", e);
      } finally {
        setExporting(false);
      }
    },
    [getBasePayload]
  );

  var warehouseOptions = useMemo(function () {
    var opts = [{ label: "All", value: "all" }];

    for (var i = 0; i < warehouses.length; i++) {
      opts.push({
        label: warehouses[i],
        value: warehouses[i],
      });
    }

    return opts;
  }, [warehouses]);

  var hubOptions = useMemo(function () {
    var opts = [{ label: "All", value: "all" }];

    for (var i = 0; i < hubLocations.length; i++) {
      opts.push({
        label: hubLocations[i],
        value: hubLocations[i],
      });
    }

    return opts;
  }, [hubLocations]);

  var selectedWarehouseOption = useMemo(function () {
    return warehouseOptions.filter(function (opt) {
      return selectedWarehouse.indexOf(opt.value) !== -1;
    });
  }, [warehouseOptions, selectedWarehouse]);

  var selectedHubOption = useMemo(function () {
    return hubOptions.filter(function (opt) {
      return selectedHubLocation.indexOf(opt.value) !== -1;
    });
  }, [hubOptions, selectedHubLocation]);

  var handleWarehouseChange = useCallback(function (selected, actionMeta) {
    var values = normalizeSelectionWithAll(selected, actionMeta);
    setSelectedWarehouse(values);
    setSelectedHubLocation(["all"]);
  }, []);

  var handleHubChange = useCallback(function (selected, actionMeta) {
    var values = normalizeSelectionWithAll(selected, actionMeta);
    setSelectedHubLocation(values);
  }, []);

  var gridKey = useMemo(function () {
    return [
      fromDate,
      toDate,
      Array.isArray(selectedWarehouse) ? selectedWarehouse.join(",") : "all",
      Array.isArray(selectedHubLocation) ? selectedHubLocation.join(",") : "all"
    ].join("__");
  }, [fromDate, toDate, selectedWarehouse, selectedHubLocation]);

  return (
    <div className="hmPage">
      <div className="hmSection">
        <DashboardHeaderBanner>
          <div className="hmHeader">
            <div className="hmTitleAccent hmAccentBlue">
              <h2 className="hmTitle">GR Level Summary Information</h2>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                type="button"
                className="hmBtn"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Exporting..." : "Export"}
              </button>

              <div className="hmRefresh">
                Last refreshed: {formatRefreshTime(lastRefreshTime)}
                {loading ? <span style={{ marginLeft: 10 }}>(Loading...)</span> : null}
              </div>
            </div>
          </div>
        </DashboardHeaderBanner>

        <div className="hmCard">
          <div
            className="hmCardHeader"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <div className="hmCardTitle">GR Level Filters</div>
            <button
              type="button"
              className="hmBtn hmBtnOutline"
              onClick={function () {
                setFilterToggle(function (prev) {
                  return !prev;
                });
              }}
            >
              {filterToggle ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {filterToggle ? (
            <div className="hmCardBody hmCardBodyCompact">
              <div className="hmFilterGrid">
                <div className="hmField">
                  <label className="hmLabel" htmlFor="from-date">From Date</label>
                  <input
                    id="from-date"
                    className="hmInput"
                    type="date"
                    value={fromDate}
                    onChange={function (e) {
                      setFromDate(e.target.value);
                    }}
                  />
                </div>

                <div className="hmField">
                  <label className="hmLabel" htmlFor="to-date">To Date</label>
                  <input
                    id="to-date"
                    className="hmInput"
                    type="date"
                    value={toDate}
                    onChange={function (e) {
                      setToDate(e.target.value);
                    }}
                  />
                </div>

                <div className="hmField">
                  <label className="hmLabel" htmlFor="warehouse">Warehouse</label>
                  <Select
                    inputId="warehouse"
                    classNamePrefix="hmReactSelect"
                    options={warehouseOptions}
                    value={selectedWarehouseOption}
                    onChange={handleWarehouseChange}
                    isMulti={true}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="hmField">
                  <label className="hmLabel" htmlFor="hub-location">Hub</label>
                  <Select
                    inputId="hub-location"
                    classNamePrefix="hmReactSelect"
                    options={hubOptions}
                    value={selectedHubOption}
                    onChange={handleHubChange}
                    isMulti={true}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isDisabled={hubLocations.length === 0}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="hmField hmFieldBtn" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="hmBtn hmBtnOutline" type="button" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="hmCard">
          <div className="hmCardHeader">
            <div className="hmCardTitle">GR Level Summary Grid</div>
            <div className="hmSubText">
              Total Records: <strong>{Number(totalRecords || 0).toLocaleString("en-IN")}</strong>
              {!selectedWarehouseIsAll ? " | Warehouse: " + selectedWarehouse.join(", ") : ""}
              {!selectedHubIsAll ? " | Hub: " + selectedHubLocation.join(", ") : ""}
            </div>
          </div>

          <div className="hmCardBody">
            <div className="ag-theme-balham" style={{ height: "72vh", width: "100%" }}>
              <AgGridReact
                key={gridKey}
                modules={AllModules}
                ref={gridRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowModelType="serverSide"
                pagination={true}
                headerHeight={45}
                rowHeight={45}
                paginationPageSize={100}
                cacheBlockSize={100}
                maxBlocksInCache={8}
                animateRows={false}
                suppressAggFuncInHeader={true}
                suppressMenuHide={false}
                rowSelection="multiple"
                rowDeselection={true}
                onGridReady={onGridReady}
                enableRangeSelection={true}
                sideBar={{
                  toolPanels: [
                    {
                      id: "columns",
                      labelDefault: "Columns",
                      labelKey: "columns",
                      iconKey: "columns",
                      toolPanel: "agColumnsToolPanel",
                      toolPanelParams: {
                        suppressPivotMode: true,
                        suppressRowGroups: true,
                        suppressValues: true,
                      },
                    },
                    {
                      id: "filters",
                      labelDefault: "Filters",
                      labelKey: "filters",
                      iconKey: "filter",
                      toolPanel: "agFiltersToolPanel",
                    },
                  ],
                }}
                statusBar={{
                  statusPanels: [{ statusPanel: "agAggregationComponent", align: "right" }],
                }}
                getContextMenuItems={function () {
                  return ["copy", "copyWithHeaders", "copyWithGroupHeaders"];
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}